import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { formatIQD, formatDate, formatDateTime } from '../../shared/format.js';
import { useAuth } from '../../shared/useAuth.js';
import { useShiftStatus, useShiftUI } from '../../shared/useShift.js';
import { PrintLogo } from '../../shared/PrintLogo.jsx';
import { useDebtsList, useDebtCustomers } from './hooks/useDebts.js';
import { SettleDebtModal, CustomerDebtsModal, StatusPill } from './components/DebtModals.jsx';
import { DebtsExportButtons } from './components/DebtsExport.jsx';
import './components/print.css';

const TABS = [
  { key: 'open', label: 'المستحقة' },
  { key: 'unpaid', label: 'غير مسددة' },
  { key: 'partial', label: 'مسددة جزئياً' },
  { key: 'paid', label: 'مسددة بالكامل' },
  { key: 'all', label: 'الكل' }
];

const POS_LABELS = {
  cafeteria: 'الكافتيريا',
  toys: 'الألعاب',
  play_fee: 'رسوم اللعب',
  checkout: 'رسوم اللعب',
  manual: 'يدوي'
};

// حد العرض المختصر، وسقف «عرض الكل» (نفس سقف الخادم في
// `debts/validators/debts.js`). ما زاد عنه يظهر منه الأحدث مع تنبيه صريح.
const PAGE_LIMIT = 200;
const ALL_LIMIT = 5000;

const STATUS_TAB_LABEL = {
  open: 'المستحقة',
  unpaid: 'غير مسددة',
  partial: 'مسددة جزئياً',
  paid: 'مسددة بالكامل',
  all: 'الكل'
};

// أعمدة التصدير والطباعة — نفس أعمدة الجدول المعروض وبنفس ترتيبها.
// المبالغ تُمرَّر أرقاماً (money) ليحملها ملف Excel كأرقام قابلة للجمع.
const DEBT_COLUMNS = [
  { label: 'الفاتورة', width: 16, value: (d) => d.number || '' },
  { label: 'العميل', width: 20, value: (d) => d.customer?.name || 'بدون عميل محدد' },
  { label: 'الهاتف', width: 14, value: (d) => d.customer?.phone || '' },
  { label: 'الطفل', width: 16, value: (d) => d.child?.name || '' },
  { label: 'النوع', width: 12, value: (d) => POS_LABELS[d.posType] || d.posType || '' },
  { label: 'تاريخ الدين', width: 18, value: (d) => formatDateTime(d.createdAt) },
  { label: 'المبلغ الأصلي', width: 15, money: true, value: (d) => Number(d.originalAmount || 0) },
  { label: 'المسدَّد', width: 15, money: true, value: (d) => Number(d.paidAmount || 0) },
  { label: 'المتبقي', width: 15, money: true, value: (d) => Number(d.remainingAmount || 0) },
  { label: 'حالة السداد', width: 14, value: (d) => d.statusLabel || d.status || '' },
  { label: 'عدد الدفعات', width: 12, value: (d) => Number(d.paymentsCount || 0) },
  { label: 'آخر دفعة', width: 18, value: (d) => (d.lastPaymentAt ? formatDateTime(d.lastPaymentAt) : '') }
];

const CUSTOMER_COLUMNS = [
  { label: 'العميل', width: 22, value: (c) => c.name || '' },
  { label: 'الهاتف', width: 14, value: (c) => c.phone || '' },
  { label: 'عدد الديون', width: 12, value: (c) => Number(c.debtsCount || 0) },
  { label: 'المبلغ الأصلي', width: 15, money: true, value: (c) => Number(c.originalTotal || 0) },
  { label: 'المسدَّد', width: 15, money: true, value: (c) => Number(c.paidTotal || 0) },
  { label: 'المتبقي', width: 15, money: true, value: (c) => Number(c.remainingTotal || 0) },
  { label: 'أقدم دين', width: 14, value: (c) => (c.oldestAt ? formatDate(c.oldestAt) : '') }
];

function StatCards({ stats }) {
  const cards = [
    { label: 'عدد الديون', value: stats ? stats.count : 0, color: '#4B5563' },
    { label: 'عدد العملاء', value: stats ? stats.customersCount : 0, color: '#0E7490' },
    { label: 'إجمالي المبالغ الأصلية', value: formatIQD(stats ? stats.originalTotal : 0), color: '#1D4ED8' },
    { label: 'المُحصَّل', value: formatIQD(stats ? stats.paidTotal : 0), color: '#15803D' },
    {
      label: 'المتبقي على العملاء',
      value: formatIQD(stats ? stats.remainingTotal : 0),
      color: stats && stats.remainingTotal > 0 ? '#B91C1C' : '#15803D'
    }
  ];
  return (
    <div className="grid grid-cols-5 gap-3">
      {cards.map((k) => (
        <div key={k.label} className="card p-3.5">
          <div className="text-[11.5px] text-ink-3">{k.label}</div>
          <div className="text-[19px] font-extrabold mt-1" style={{ color: k.color }}>{k.value}</div>
        </div>
      ))}
    </div>
  );
}

export function DebtsScreen() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canSettle = hasPermission('debts.settle');
  const { shift, blocked } = useShiftStatus();
  const openShiftModal = useShiftUI((s) => s.openModal);

  const [status, setStatus] = useState('open');
  const [view, setView] = useState('debts');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [settling, setSettling] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  // «عرض الكل»: قرار مستقل عن الفلاتر — يبقى مفعّلاً عند تغيير التبويب أو
  // البحث أو التاريخ، فلا ترجع الشاشة للعرض المختصر من تلقاء نفسها.
  const [showAll, setShowAll] = useState(false);

  const params = useMemo(() => {
    const p = { status, limit: showAll ? ALL_LIMIT : PAGE_LIMIT };
    if (search.trim()) p.q = search.trim();
    if (from) p.from = new Date(`${from}T00:00:00`).toISOString();
    if (to) p.to = new Date(`${to}T23:59:59`).toISOString();
    return p;
  }, [status, search, from, to, showAll]);

  const listQ = useDebtsList(params);
  const custQ = useDebtCustomers(params, view === 'customers');

  const debts = listQ.data?.debts || [];
  const stats = listQ.data?.stats;
  const customers = custQ.data?.customers || [];

  const total = Number(listQ.data?.total || 0);
  // تجاوز السقف: الخادم أعاد الأحدث فقط — نقولها صراحةً بدل أن يظن المستخدم
  // أن ما يراه هو كل شيء.
  const cappedAll = showAll && total > debts.length;

  const isCustomersView = view === 'customers';
  const exportRows = isCustomersView ? customers : debts;
  const exportColumns = isCustomersView ? CUSTOMER_COLUMNS : DEBT_COLUMNS;

  // سطور تُطبع وتُكتب في رأس ملف Excel: ما هي الفلاتر التي أنتجت هذه الأرقام.
  const filterLines = useMemo(() => {
    const lines = [`الحالة: ${STATUS_TAB_LABEL[status] || status}`];
    lines.push(`العرض: ${isCustomersView ? 'حسب العميل' : 'حسب الفاتورة'}`);
    if (search.trim()) lines.push(`بحث: ${search.trim()}`);
    if (from || to) lines.push(`المدة: ${from || '—'} إلى ${to || '—'}`);
    lines.push(`عدد السجلات المعروضة: ${exportRows.length}`);
    lines.push(`تاريخ الإصدار: ${formatDateTime(new Date())}`);
    return lines;
  }, [status, isCustomersView, search, from, to, exportRows.length]);

  const exportTitle = isCustomersView ? 'ديون العملاء — حسب العميل' : 'ديون العملاء — حسب الفاتورة';
  const exportName = `debts-${isCustomersView ? 'customers' : 'invoices'}-${status}-${new Date().toISOString().slice(0, 10)}`;

  function submitSearch(e) {
    e.preventDefault();
    setSearch(q);
  }

  return (
    <div className="space-y-3.5">
      <div className="no-print space-y-3.5">
      {blocked ? (
        <div className="card p-3.5 flex items-center gap-3" style={{ background: '#FFF7ED', borderColor: '#FED7AA' }}>
          <Icon name="clock" size={16} color="#EA580C" />
          <div className="flex-1 text-[13px]">
            لا يوجد لديك شفت مفتوح — تحصيل الدين نقداً يتطلب شفتاً مفتوحاً ليدخل المال في درجك.
          </div>
          <button type="button" className="btn btn-primary" onClick={() => openShiftModal('open')}>فتح شفت</button>
        </div>
      ) : shift ? (
        <div className="card p-3 flex items-center gap-3 text-[12.5px]" style={{ background: '#F0FDF4', borderColor: '#BBF7D0' }}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#22C55E' }} />
          <div className="flex-1">
            كل تحصيل نقدي يدخل درج شفتك <b>{shift.cashboxName}</b> ويظهر في تقرير الشفت والقاصة والتقارير المالية.
          </div>
        </div>
      ) : null}
      </div>

      {/* جذر الطباعة: ما بداخله وحده هو ما يخرج على الورق (انظر print.css). */}
      <div className="print-root space-y-3.5">
        {/* شعار المركز من الإعدادات — أعلى الورقة ووسطها */}
        <PrintLogo />
        <div className="print-only" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: '14pt', fontWeight: 800 }}>{exportTitle}</h2>
          <div style={{ fontSize: '9pt', marginTop: 4 }}>{filterLines.join('  •  ')}</div>
        </div>

      <StatCards stats={stats} />

      <Card>
        <form className="no-print flex items-center gap-2 flex-wrap mb-3" onSubmit={submitSearch}>
          <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px]">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setStatus(t.key)}
                className={clsx(
                  'px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold',
                  status === t.key ? 'bg-orange text-white' : 'text-ink-2 hover:bg-white'
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px]">
            {[{ key: 'debts', label: 'حسب الفاتورة' }, { key: 'customers', label: 'حسب العميل' }].map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => setView(v.key)}
                className={clsx(
                  'px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold',
                  view === v.key ? 'bg-orange text-white' : 'text-ink-2 hover:bg-white'
                )}
              >
                {v.label}
              </button>
            ))}
          </div>

          <input
            className="input"
            style={{ width: 280 }}
            placeholder="بحث باسم العميل أو هاتفه أو رقم الفاتورة..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" className="btn"><Icon name="search" size={13} /> بحث</button>
          {search ? (
            <button type="button" className="btn btn-ghost" onClick={() => { setQ(''); setSearch(''); }}>
              <Icon name="x" size={13} /> مسح
            </button>
          ) : null}

          <label className="text-[12px] text-ink-3">من</label>
          <input type="date" className="input" style={{ width: 145 }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <label className="text-[12px] text-ink-3">إلى</label>
          <input type="date" className="input" style={{ width: 145 }} value={to} onChange={(e) => setTo(e.target.value)} />

          <div className="flex items-center gap-2 flex-wrap ms-auto">
            <button
              type="button"
              className={clsx('btn', showAll && 'btn-primary')}
              onClick={() => setShowAll((v) => !v)}
              title={showAll
                ? 'العودة للعرض المختصر'
                : `عرض كل السجلات المطابقة للفلاتر الحالية (حتى ${ALL_LIMIT} سجل)`}
            >
              <Icon name="chevronD" size={13} /> {showAll ? 'عرض مختصر' : 'عرض الكل'}
            </button>
            <DebtsExportButtons
              rows={exportRows}
              columns={exportColumns}
              filename={exportName}
              title={exportTitle}
              meta={filterLines}
              disabled={listQ.isLoading || (isCustomersView && custQ.isLoading)}
            />
            <button
              type="button"
              className="btn"
              onClick={() => { listQ.refetch(); if (view === 'customers') custQ.refetch(); }}
            >
              <Icon name="clock" size={13} /> تحديث
            </button>
          </div>
        </form>

        {view === 'customers' ? (
          custQ.isLoading ? (
            <div className="text-center py-10 text-ink-3">جاري التحميل...</div>
          ) : !customers.length ? (
            <div className="text-center py-10 text-ink-3">لا ديون على العملاء</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-ink-3 border-b border-line text-right">
                    <th className="p-2">العميل</th>
                    <th className="p-2">الهاتف</th>
                    <th className="p-2">عدد الديون</th>
                    <th className="p-2">المبلغ الأصلي</th>
                    <th className="p-2">المسدَّد</th>
                    <th className="p-2">المتبقي</th>
                    <th className="p-2">أقدم دين</th>
                    <th className="p-2 no-print">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr key={String(c.guardianId || 'none')} className="border-b border-line">
                      <td className="p-2 font-bold">{c.name}</td>
                      <td className="p-2 font-mono text-ink-2">{c.phone || '—'}</td>
                      <td className="p-2">{c.debtsCount}</td>
                      <td className="p-2">{formatIQD(c.originalTotal)}</td>
                      <td className="p-2 text-green font-semibold">{formatIQD(c.paidTotal)}</td>
                      <td className="p-2 font-extrabold" style={{ color: c.remainingTotal ? '#B91C1C' : '#15803D' }}>
                        {formatIQD(c.remainingTotal)}
                      </td>
                      <td className="p-2 text-ink-3">{formatDate(c.oldestAt)}</td>
                      <td className="p-2 no-print">
                        {c.guardianId ? (
                          <button type="button" className="btn" onClick={() => setCustomerId(String(c.guardianId))}>
                            <Icon name="reports" size={13} /> السجل
                          </button>
                        ) : <span className="text-ink-3">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : listQ.isLoading ? (
          <div className="text-center py-10 text-ink-3">جاري التحميل...</div>
        ) : !debts.length ? (
          <div className="text-center py-10 text-ink-3">
            {search ? 'لا نتائج مطابقة للبحث' : 'لا ديون على العملاء'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-ink-3 border-b border-line text-right">
                  <th className="p-2">الفاتورة</th>
                  <th className="p-2">العميل</th>
                  <th className="p-2">الهاتف</th>
                  <th className="p-2">الطفل</th>
                  <th className="p-2">النوع</th>
                  <th className="p-2">تاريخ الدين</th>
                  <th className="p-2">المبلغ الأصلي</th>
                  <th className="p-2">المسدَّد</th>
                  <th className="p-2">المتبقي</th>
                  <th className="p-2">الحالة</th>
                  <th className="p-2 no-print">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {debts.map((d) => (
                  <tr key={String(d._id)} className="border-b border-line">
                    <td className="p-2 font-mono">{d.number}</td>
                    <td className="p-2 font-bold">{d.customer?.name || 'بدون عميل محدد'}</td>
                    <td className="p-2 font-mono text-ink-2">{d.customer?.phone || '—'}</td>
                    <td className="p-2">{d.child?.name || '—'}</td>
                    <td className="p-2 text-ink-3">{POS_LABELS[d.posType] || d.posType}</td>
                    <td className="p-2 text-ink-3">{formatDateTime(d.createdAt)}</td>
                    <td className="p-2">{formatIQD(d.originalAmount)}</td>
                    <td className="p-2 text-green font-semibold">
                      {formatIQD(d.paidAmount)}
                      {d.paymentsCount ? <span className="text-ink-3 text-[11px] ms-1">({d.paymentsCount} دفعة)</span> : null}
                    </td>
                    <td className="p-2 font-extrabold" style={{ color: d.remainingAmount ? '#B91C1C' : '#15803D' }}>
                      {formatIQD(d.remainingAmount)}
                    </td>
                    <td className="p-2"><StatusPill status={d.status} label={d.statusLabel} /></td>
                    <td className="p-2 no-print">
                      <div className="flex gap-1">
                        {d.remainingAmount > 0 && canSettle ? (
                          <button type="button" className="btn btn-primary" onClick={() => setSettling(d)}>
                            <Icon name="check" size={13} /> تسديد
                          </button>
                        ) : null}
                        {d.customer?.guardianId ? (
                          <button
                            type="button"
                            className="btn"
                            title="سجل العميل والدفعات"
                            onClick={() => setCustomerId(String(d.customer.guardianId))}
                          >
                            <Icon name="reports" size={13} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {cappedAll ? (
              <div
                className="text-[12px] mt-2 rounded-[8px] p-2.5 font-semibold"
                style={{ background: '#FFF7ED', color: '#92400E' }}
              >
                عدد السجلات المطابقة {total} ويتجاوز الحد الأقصى للعرض ({ALL_LIMIT} سجل) —
                يُعرض الأحدث {debts.length} فقط. ضيّق الفلتر (التاريخ أو البحث أو حالة السداد)
                لرؤية الباقي. الطباعة والتصدير يشملان المعروض هنا.
              </div>
            ) : total > debts.length ? (
              <div className="text-[11.5px] text-ink-3 mt-2">
                يُعرض {debts.length} من {total} — اضغط «عرض الكل» لعرضها جميعاً بالفلاتر نفسها.
              </div>
            ) : (
              <div className="text-[11.5px] text-ink-3 mt-2">
                يُعرض {debts.length} سجلاً — كل السجلات المطابقة للفلاتر الحالية.
              </div>
            )}
          </div>
        )}
      </Card>
      </div>

      {settling ? (
        <SettleDebtModal debt={settling} onClose={() => setSettling(null)} />
      ) : null}

      {customerId ? (
        <CustomerDebtsModal
          guardianId={customerId}
          onSettle={canSettle ? (d) => { setCustomerId(null); setSettling(d); } : null}
          onClose={() => setCustomerId(null)}
        />
      ) : null}
    </div>
  );
}
